import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private static readonly DEFAULT_PRIMARY = '#151945';
  private static readonly LUMINANCE_THRESHOLD = 0.5;

  private readonly _primary$ = new BehaviorSubject<string>(ThemeService.DEFAULT_PRIMARY);
  /** Emits the current primary color string whenever it changes. */
  readonly primary$ = this._primary$.asObservable();

  /** Returns the current primary color string synchronously. */
  getPrimary(): string {
    return this._primary$.getValue();
  }

  /**
   * Sets the primary theme color and automatically derives a contrasting
   * foreground color exposed as `--mona-on-primary`.
   */
  setPrimary(color: string | null | undefined): void {
    const primary = this.normalizeHex(color) ?? ThemeService.DEFAULT_PRIMARY;
    const onPrimary = this.getContrastColor(primary);
    const isDarkPrimary = onPrimary === '#ffffff';
    // Hover tint: lighten dark primaries, darken light primaries (matches btn hover style).
    const hoverPrimary = isDarkPrimary
      ? 'color-mix(in srgb, var(--mona-primary) 15%, white)'
      : 'color-mix(in srgb, var(--mona-primary) 60%, white)';
    // Stronger active/pressed tint, used e.g. for selected pagination buttons.
    const hoverActivePrimary = isDarkPrimary
      ? 'color-mix(in srgb, var(--mona-primary) 20%, white)'
      : 'color-mix(in srgb, var(--mona-primary) 80%, white)';
    // Filter for monochrome <img> icons that should match `--mona-on-primary`.
    // Black icons -> white when foreground is white, stays black when foreground is black.
    const onPrimaryFilter =
      onPrimary === '#ffffff'
        ? 'brightness(0) invert(1)'
        : 'brightness(0)';

    const root = document.documentElement;
    root.style.setProperty('--mona-primary', primary);
    root.style.setProperty('--mona-on-primary', onPrimary);
    root.style.setProperty('--mona-hover-primary', hoverPrimary);
    root.style.setProperty('--mona-hover-active-primary', hoverActivePrimary);
    root.style.setProperty('--mona-on-primary-filter', onPrimaryFilter);

    this._primary$.next(primary);
  }

  /**
   * Returns either '#000000' or '#ffffff' based on the WCAG relative
   * luminance of the given hex color, so text stays readable on any
   * background.
   */
  getContrastColor(hex: string): '#000000' | '#ffffff' {
    const normalized = this.normalizeHex(hex);
    if (!normalized) {
      return '#ffffff';
    }

    const c = normalized.replace('#', '');
    const r = parseInt(c.substring(0, 2), 16) / 255;
    const g = parseInt(c.substring(2, 4), 16) / 255;
    const b = parseInt(c.substring(4, 6), 16) / 255;

    const toLinear = (v: number): number =>
      v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);

    const luminance =
      0.2126 * toLinear(r) +
      0.7152 * toLinear(g) +
      0.0722 * toLinear(b);

    return luminance > ThemeService.LUMINANCE_THRESHOLD ? '#000000' : '#ffffff';
  }

  /**
   * Accepts `#rgb`, `#rrggbb` (with or without leading `#`) and returns a
   * canonical `#rrggbb` lowercase string. Returns null for invalid input.
   */
  private normalizeHex(value: string | null | undefined): string | null {
    if (!value) {
      return null;
    }
    const trimmed = value.trim().replace(/^#/, '');
    if (!/^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimmed)) {
      return null;
    }
    const full =
      trimmed.length === 3
        ? trimmed
            .split('')
            .map((ch) => ch + ch)
            .join('')
        : trimmed;
    return `#${full.toLowerCase()}`;
  }
}
